import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector } from "react-redux";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import FormModal, { formFieldSx } from "../common/FormModal/FormModal";
import { useCreateCategory } from "../../hooks/api/useCategory";
import type { CategoryFormData } from "../Category/types/categoryTypes";
import { RootState } from "../../store/store";
import {
  buildQuickCategorySchema,
  QuickCategoryFormValues
} from "./quickCategorySchema";

const QUICK_ADD_CATEGORY_FORM_ID = "quick-add-category-form";

export interface QuickAddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onCategoryCreated: (category: { id: string; name: string }) => void;
}

const QuickAddCategoryModal: React.FC<QuickAddCategoryModalProps> = ({
  open,
  onClose,
  onCategoryCreated
}) => {
  const { t } = useKivunovaTranslation();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const createCategory = useCreateCategory();
  const [submitApiError, setSubmitApiError] = useState<string | null>(null);

  const categoryList = useSelector(
    (state: RootState) => state.categories.categoryList
  );

  const schema = useMemo(() => buildQuickCategorySchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<QuickCategoryFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema as any),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      parentId: ""
    }
  });

  useEffect(() => {
    if (!open) return;
    setSubmitApiError(null);
    reset({
      name: "",
      description: "",
      parentId: ""
    });
  }, [open, reset]);

  const onSubmit: SubmitHandler<QuickCategoryFormValues> = async (data) => {
    const parentTrimmed = (data.parentId ?? "").trim();
    const payload: CategoryFormData = {
      name: data.name.trim(),
      ...(data.description?.trim()
        ? { description: data.description.trim() }
        : {}),
      ...(parentTrimmed ? { parentId: parentTrimmed } : {})
    };

    try {
      const id = await createCategory.mutateAsync(payload);
      if (id) {
        onCategoryCreated({ id, name: data.name.trim() });
        onClose();
      } else {
        setSubmitApiError(
          t(
            "quick-add-category-failed",
            "Could not create category. Please try again."
          )
        );
      }
    } catch {
      setSubmitApiError(
        t(
          "quick-add-category-failed",
          "Could not create category. Please try again."
        )
      );
    }
  };

  const busy = isSubmitting || createCategory.isPending;

  const handleModalClose = () => {
    if (!busy) onClose();
  };

  return (
    <FormModal
      open={open}
      onClose={handleModalClose}
      title={t("quick-add-category-title", "New category")}
      maxWidth="sm"
      dataTestId="quick-add-category-modal"
      footer={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            minWidth: 0,
            flex: "1 1 100%"
          }}
        >
          <LoadingButton
            form={QUICK_ADD_CATEGORY_FORM_ID}
            type="submit"
            variant="contained"
            loading={busy}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              backgroundColor: BRAND_COLOR,
              boxShadow: "none",
              "&:hover": { backgroundColor: BRAND_HOVER, boxShadow: "none" }
            }}
          >
            {t("quick-add-category-save", "Create category")}
          </LoadingButton>
          <Button
            type="button"
            variant="outlined"
            onClick={handleModalClose}
            disabled={busy}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            {t("cancel", "Cancel")}
          </Button>
        </Box>
      }
    >
      <Box
        component="form"
        id={QUICK_ADD_CATEGORY_FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{
          ...formFieldSx,
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        {submitApiError ? (
          <Alert
            severity="error"
            onClose={() => setSubmitApiError(null)}
            sx={{ mb: 0.5 }}
          >
            {submitApiError}
          </Alert>
        ) : null}

        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size={matchMD ? "small" : "medium"}
              label={t("category-name", "Category Name")}
              placeholder={t(
                "category-name-placeholder",
                "Enter category name"
              )}
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={busy}
              autoFocus
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size={matchMD ? "small" : "medium"}
              label={t("description", "Description")}
              placeholder={t(
                "category-description-placeholder",
                "Enter category description"
              )}
              multiline
              rows={2}
              error={!!errors.description}
              helperText={errors.description?.message}
              disabled={busy}
            />
          )}
        />

        <Controller
          name="parentId"
          control={control}
          render={({ field }) => (
            <FormControl
              fullWidth
              size={matchMD ? "small" : "medium"}
              error={!!errors.parentId}
              disabled={busy}
            >
              <InputLabel id="quick-add-category-parent-label" shrink>
                {t("parent-category", "Parent Category")}
              </InputLabel>
              <Select
                {...field}
                labelId="quick-add-category-parent-label"
                label={t("parent-category", "Parent Category")}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value || "")}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography component="span" color="text.secondary">
                        {t(
                          "select-parent-category-optional",
                          "Select parent category (optional)"
                        )}
                      </Typography>
                    );
                  }
                  return (
                    categoryList.find((c) => c.id === selected)?.name ??
                    selected
                  );
                }}
              >
                <MenuItem value="">
                  <em>
                    {t("none-top-level-category", "None (top-level category)")}
                  </em>
                </MenuItem>
                {categoryList.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.parentId && (
                <FormHelperText>{errors.parentId.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </Box>
    </FormModal>
  );
};

export default QuickAddCategoryModal;
